import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/utils/apiClient";
import { useAuth } from "@/state/auth";
import type {
  ContentType,
  GenerationStatus,
  LogFull,
  LogRow,
} from "@/types/content";

export interface GenerationLogFilters {
  content_type?: ContentType;
  status?: GenerationStatus;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

export interface GenerationLogListResponse {
  items: LogRow[];
  total: number;
}

const buildQuery = (filters?: GenerationLogFilters): string => {
  if (!filters) return "";
  const params = new URLSearchParams();
  if (filters.content_type) params.set("content_type", filters.content_type);
  if (filters.status) params.set("status", filters.status);
  if (filters.date_from) params.set("date_from", filters.date_from);
  if (filters.date_to) params.set("date_to", filters.date_to);
  if (filters.limit != null) params.set("limit", String(filters.limit));
  if (filters.offset != null) params.set("offset", String(filters.offset));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
};

const fetchLogs = async (
  filters?: GenerationLogFilters
): Promise<GenerationLogListResponse> => {
  return apiRequest<GenerationLogListResponse>(
    `/api/admin/generation-logs${buildQuery(filters)}`
  );
};

const fetchLog = async (id: number): Promise<LogFull | null> => {
  const { log } = await apiRequest<{ log: LogFull }>(
    `/api/admin/generation-logs/${id}`
  );
  return log;
};

export const useGenerationLogs = (filters?: GenerationLogFilters) => {
  const profile = useAuth((state) => state.profile);
  return useQuery({
    queryKey: ["generation-logs", "list", filters ?? {}],
    queryFn: () => fetchLogs(filters),
    staleTime: 15_000,
    enabled: Boolean(profile),
  });
};

export const useGenerationLog = (id: number) => {
  const profile = useAuth((state) => state.profile);
  return useQuery({
    queryKey: ["generation-logs", "detail", id],
    queryFn: () => fetchLog(id),
    enabled: Boolean(profile && Number.isFinite(id)),
    staleTime: 0,
  });
};
